import { RestError } from '@azure/core-http';
import { Container } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { LoadingIndicator } from '../../ui/components/pure/loading-indicator/loading-indicator.component';
import { ErrorFullPage } from '../../ui/components/pure/errors/error-full-page/error-full-page.component';
import { UserDetailsPage } from '../../features/users/details/user-details.page';
import { UsersListPage } from '../../features/users/list/users-list.page';
import { CouriersListPage } from '../../features/couriers/list/couriers-list.page';
import { CarriersListPage } from '../../features/carriers/list/carriers-list.page';
import { CarrierDetailsPage } from '../../features/carriers/details/carrier-details.page';
import { IncompleteCouriersPage } from '../../features/couriers/incomplete/incomplete-couriers.page';
import { PendingCouriersPage } from '../../features/couriers/pending/pending-couriers.page';
import { CourierDetailsPage } from '../../features/couriers/details/courier-details.page';
import { DashboardPage } from '../../features/dashboard/dashboard.page';
import { TeamMemberDetailsPage } from '../../features/team/details/team-member-details.page';
import { TeamMembersListPage } from '../../features/team/list/team-members-list.page';
import { OrderDetailsPage } from '../../features/orders/details/order-details.page';
import { OrdersListPage } from '../../features/orders/list/orders-list.page';
import { createPath, ProjectPathParams, Paths } from './app.router';
import { Layout } from '../layout/layout.component';
import { useProjectShortcodeMap } from './useProjectShortcodeMap';
import { HubsList } from '../../features/lockers/list/hubs-list.page';
import { LockerDetailsPage } from '../../features/lockers/details/locker-details.page';
import { LockerUtilisationReportPage } from '../../features/reports/locker-utilisation/locker-utilisation-report.page';
import { DeliveryContainerUtilisationReportPage } from '../../features/reports/delivery-container-utilisation/delivery-container-utilisation-report.page';
import { DeliveriesListPage } from '../../features/deliveries/list/deliveries-list.page';
import { DeliveriesDetailsPage } from '../../features/deliveries/details/deliveries-details.page';
import { PermissionsPage } from '../../features/permissions/permissions-page';
import { DeliveriesRequiringAttentionPage } from '../../features/reports/deliveries-requiring-attention/deliveries-requiring-attention.page';
import { WelcomePage } from '../../features/welcome/welcome.page';
import { CourierLoginTestingPage } from '../../features/reports/courier-login-testing/courier-login-testing.page';
import { SettingsPage } from '../../features/settings/settings.page';
import { DeliveriesInternalPage } from '../../features/deliveries/deliveries-internal/deliveries-internal-page';
import { ProjectProvider } from '../../core/context/project.context';
import { useQueryGetProject } from '../../core/queries/projects.queries';
import { isCarrierProjectType } from '../navigation/courier-project-type';
import { KioskStatusReportPage } from '../../features/reports/kiosk-status/kiosk-status-report.page';
import { KioskDetailView } from '../../features/reports/kiosk-status/components/kiosk-detail-view.component';

export function ProjectRouter({ projectID }: { projectID: string }) {
  const [uuids, shortcodes, projects] = useProjectShortcodeMap();
  const isLoadingShortcodeMap = projects.isLoading;

  const [uuid, shortcode] = useMemo(
    () => [uuids[projectID], shortcodes[projectID]] as const,
    [uuids, shortcodes, projectID]
  );

  // If the page has been loaded using a url containing the uuid of the project
  // transform the url to use the shortcode instead
  const [transformed, setTransformed] = useState(false);
  const history = useHistory();
  useEffect(() => {
    if (
      !transformed &&
      !isLoadingShortcodeMap &&
      typeof uuid !== 'undefined' &&
      projectID === uuid &&
      typeof shortcode !== 'undefined'
    ) {
      setTransformed(true);
      history.replace(`${history.location.pathname.replace(projectID, shortcode)}${history.location.search}`);
    }
  }, [isLoadingShortcodeMap, uuid, shortcode, projectID, transformed, history]);

  // This function will add the projectID and shortcode to the match params.
  // Note: this function doesn't get called until the url param has resolved to a uuid
  const transform = useCallback(
    function transformParams<T extends Record<string, string>>(params: T): T & { shortcode?: string } {
      return {
        ...params,
        projectID: uuid || projectID,
        shortcode
      };
    },
    [uuid, shortcode, projectID]
  );

  if (isLoadingShortcodeMap) {
    return (
      <Layout>
        <FullPageLoadingIndicator />
      </Layout>
    );
  }

  // Without its uuid the url param can't be sent to the api, which only accepts uuids. A shortcode
  // would be rejected as forbidden whoever the caller is.
  if (typeof uuid === 'undefined') {
    // A refetch may be adding a project that was only just created
    if (projects.isFetching) {
      return (
        <Layout>
          <FullPageLoadingIndicator />
        </Layout>
      );
    }

    // Without a list we can't tell a missing project from a failed request
    if (projects.error) {
      return (
        <Layout>
          {(projects.error as RestError).statusCode === 401 ? (
            <ErrorFullPage preset="GLOBAL_ACCESS_DENIED" />
          ) : (
            <ErrorFullPage />
          )}
        </Layout>
      );
    }

    // The list holds every project the user can see, so this is an invalid url param or one they don't have access to
    return (
      <Layout>
        <ErrorFullPage preset="ACCESS_DENIED" entity="project" />
      </Layout>
    );
  }

  const params = transform({ projectID });
  return (
    <ProjectProvider projectId={params.projectID}>
      <Layout params={params}>
        <Switch>
          <Route>
            <Switch>
              <Route
                exact
                path={Paths.welcome}
                render={({ match }) => {
                  return <WelcomePage params={transform(match.params)} />;
                }}
              />
              <Route
                exact
                path={Paths.dashboard}
                render={({ match }) => {
                  return <DashboardRoute params={transform(match.params)} />;
                }}
              />
              <Route
                exact
                path={Paths.hubs}
                render={({ match }) => {
                  return <HubsList params={transform(match.params)} />;
                }}
              />
              <Route
                exact
                path={Paths.container}
                render={({ match }) => {
                  return <LockerDetailsPage params={transform(match.params)} />;
                }}
              />
              <Route
                exact
                path={Paths.customers}
                render={({ match }) => {
                  return <UsersListPage params={transform(match.params)} />;
                }}
              />
              <Route
                exact
                path={Paths.customer}
                render={({ match }) => {
                  return <UserDetailsPage params={transform(match.params)} />;
                }}
              />
              <Route
                exact
                path={Paths.customerOfChildProject}
                render={({ match }) => {
                  return <UserDetailsPage params={transform(match.params)} />;
                }}
              />
              <Route
                exact
                path={Paths.courier}
                render={({ match }) => {
                  return <CourierDetailsPage params={transform(match.params)} />;
                }}
              />
              <Route
                exact
                path={Paths.couriers}
                render={({ match }) => {
                  return <CouriersListPage params={transform(match.params)} />;
                }}
              />
              <Route
                exact
                path={Paths.carriers}
                render={({ match }) => {
                  return <CarriersListPage key="carriers" params={transform(match.params)} />;
                }}
              />
              <Route
                exact
                path={Paths.pendingCarriers}
                render={({ match }) => {
                  return <CarriersListPage key="pendingCarriers" params={transform(match.params)} pending />;
                }}
              />
              <Route
                exact
                path={Paths.carrier}
                render={({ match }) => {
                  return <CarrierDetailsPage params={transform(match.params)} />;
                }}
              />
              <Route
                exact
                path={Paths.pendingCouriers}
                render={({ match }) => {
                  return <PendingCouriersPage params={transform(match.params)} />;
                }}
              />
              <Route
                exact
                path={Paths.incompleteCouriers}
                render={({ match }) => {
                  return <IncompleteCouriersPage params={transform(match.params)} />;
                }}
              />
              <Route
                exact
                path={Paths.team}
                render={({ match }) => {
                  return <TeamMembersListPage params={transform(match.params)} />;
                }}
              />
              <Route
                exact
                path={Paths.teamMember}
                render={({ match }) => {
                  return <TeamMemberDetailsPage params={transform(match.params)} />;
                }}
              />
              <Route
                exact
                path={Paths.orders}
                render={({ match }) => {
                  return <OrdersListPage params={transform(match.params)} />;
                }}
              />
              <Route
                exact
                path={Paths.order}
                render={({ match }) => {
                  return <OrderDetailsPage params={transform(match.params)} />;
                }}
              />
              <Route
                exact
                path={Paths.deliveries}
                render={({ match }) => {
                  return <DeliveriesListPage params={transform(match.params)} />;
                }}
              />
              <Route
                exact
                path={Paths.deliveriesInternal}
                render={({ match }) => {
                  return <DeliveriesInternalPage params={transform(match.params)} />;
                }}
              />

              <Route
                exact
                path={Paths.delivery}
                render={({ match }) => {
                  return <DeliveriesDetailsPage params={transform(match.params)} />;
                }}
              />
              <Route
                exact
                path={Paths.lockerUtilisationReport}
                render={({ match }) => {
                  return <LockerUtilisationReportPage params={transform(match.params)} />;
                }}
              />
              <Route
                exact
                path={Paths.deliveryContainerUtilisationReport}
                render={({ match }) => {
                  return <DeliveryContainerUtilisationReportPage params={transform(match.params)} />;
                }}
              />
              <Route
                exact
                path={Paths.deliveriesRequiringAttention}
                render={({ match }) => {
                  return <DeliveriesRequiringAttentionPage params={transform(match.params)} />;
                }}
              />
              <Route
                exact
                path={Paths.courierLoginTesting}
                render={() => {
                  return <CourierLoginTestingPage />;
                }}
              />
              <Route
                exact
                path={Paths.permissions}
                render={({ match }) => {
                  return <PermissionsPage params={transform(match.params)} />;
                }}
              />
              <Route
                exact
                path={Paths.settings}
                render={({ match }) => {
                  return <SettingsPage params={transform(match.params)} />;
                }}
              />
              <Route
                exact
                path={Paths.kioskConnectionStatusReport}
                render={({ match }) => {
                  return <KioskStatusReportPage params={transform(match.params)} />;
                }}
              />
              <Route
                exact
                path={Paths.kioskConnectionStatusDetail}
                render={({ match }) => {
                  return <KioskDetailView params={transform(match.params)} />;
                }}
              />
              <Route
                render={() => {
                  return <ProjectHome params={transform({ projectID })} />;
                }}
              />
            </Switch>
          </Route>
        </Switch>
      </Layout>
    </ProjectProvider>
  );
}

/**
 * Hiding the nav item leaves the route reachable by URL, bookmark or back button, so a carrier or
 * region project is redirected the same way `ProjectHome` sends it — it has no dashboard to render.
 */
const DashboardRoute = ({ params }: { params: ProjectPathParams<Paths.dashboard> }) => {
  const project = useQueryGetProject(params.projectID);

  if (project.isLoading) {
    return <FullPageLoadingIndicator />;
  }

  // Handled here rather than in DashboardPage: mounting it refetches the failed query, which puts this
  // back into loading, unmounts it, and starts the cycle again.
  if (project.error) {
    if (project.error.statusCode === 401) {
      return <ErrorFullPage preset="GLOBAL_ACCESS_DENIED" />;
    }

    if (project.error.statusCode === 403) {
      return <ErrorFullPage preset="ACCESS_DENIED" entity="project" />;
    }

    if (project.error.statusCode === 404) {
      return <ErrorFullPage preset="NOT_FOUND" entity="project" />;
    }

    return <ErrorFullPage />;
  }

  if (isCarrierProjectType(project.data?.projectType)) {
    return <Redirect to={createPath(Paths.couriers, { projectID: params.shortcode || params.projectID })} />;
  }

  return <DashboardPage params={params} />;
};

const ProjectHome = ({ params }: { params: ProjectPathParams<Paths.project> }) => {
  const projectID = params.projectID;
  const project = useQueryGetProject(projectID);

  // Nothing to decide until the project loads — a guessed redirect would strand the visitor.
  if (project.isLoading) {
    return <FullPageLoadingIndicator />;
  }

  let redirectPath = Paths.dashboard;

  // Temporary hack to redirect users with a root project selected to the welcome page
  if (internalProjectIDs.includes(projectID)) {
    redirectPath = Paths.welcome;
  } else if (isCarrierProjectType(project.data?.projectType)) {
    // A carrier or region project has no dashboard — its couriers are the whole of it.
    redirectPath = Paths.couriers;
  }

  return <Redirect to={createPath(redirectPath, { projectID: params.shortcode || params.projectID })} />;
};

function FullPageLoadingIndicator() {
  return (
    <Container style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '95vh' }}>
      <LoadingIndicator />
    </Container>
  );
}

// Temporary hack to redirect users with a root project selected to the welcome page
export const internalProjectIDs = [
  // Production
  'd8710a9f-cb03-4e7f-84bf-1d81085a6ffc', // LKV
  '57a3a537-ab59-495b-8e9a-6b33893f1ea5', // Groundfloor
  'd3368861-b725-4504-8a78-bb22edac2eb5', // Groundfloor Australia
  '44836864-78e1-462b-8526-f1e67d649f71', // Groundfloor New Zealand
  'ca7e510d-1424-42a1-9d8f-cc87448ac97b', // Retail

  // Development
  '15d2c604-7a4e-4766-a5a1-a6c699a72750', // LKV
  'dc061d69-df77-4d01-b40e-f7ea6ac34e98', // Groundfloor
  '1b1caf3c-bb5e-4de4-9bea-35724a577476', // Groundfloor Australia
  '78f925b2-272e-4b29-be36-fc41d5c1dc5a', // Groundfloor New Zealand
  '418c3b0e-7340-4ec8-9b35-6056c379deef' // Retail
];
